import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { PRODUCT_TYPE } from '../config/product';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Set up axios interceptor for auth token
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      loadUser();
    } else {
      setLoading(false);
    }
  }, []);

  const loadUser = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/auth/me?product=${PRODUCT_TYPE}`);
      setUser(response.data);
    } catch (err) {
      console.error('Failed to load user:', err);
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    setError(null);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/login`, {
        email,
        password,
      });

      const { token, user: userData } = response.data;

      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(userData);

      return { success: true };
    } catch (err) {
      const message = err.response?.data?.error || 'Login failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const register = async (userData) => {
    setError(null);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/register`, userData);

      const { token, user: newUser } = response.data;

      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(newUser);

      return { success: true };
    } catch (err) {
      const message = err.response?.data?.error || 'Registration failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const isOwner = () => user?.role === 'owner';
  const isAdmin = () => ['owner', 'admin'].includes(user?.role);
  const isCrewLead = () => ['owner', 'admin', 'crew_lead'].includes(user?.role);
  const isCrewLeadOnly = () => user?.role === 'crew_lead';
  const isDriver = () => user?.role === 'driver';
  const isSuperAdmin = () => user?.isSuperAdmin === true;
  // Helper for hiding customer prices (drivers and crew leads can't see prices)
  const shouldHidePrices = () => user?.role === 'driver' || user?.role === 'crew_lead';

  // Trial status helpers
  const TRIAL_STORM_LIMIT = 1;
  const isOnTrial = () => user?.subscriptionStatus === 'trial';
  const getStormEventsUsed = () => user?.stormEventsUsed || 0;
  const isTrialExpired = () => isOnTrial() && getStormEventsUsed() >= TRIAL_STORM_LIMIT;
  const canPerformStormActions = () => !isTrialExpired();

  // Tier helpers
  const isPro = () => ['pro', 'premium', 'enterprise'].includes(user?.tier);

  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    isOwner,
    isAdmin,
    isCrewLead,
    isCrewLeadOnly,
    isDriver,
    isSuperAdmin,
    shouldHidePrices,
    refreshUser: loadUser,
    // Trial helpers
    isOnTrial,
    getStormEventsUsed,
    isTrialExpired,
    canPerformStormActions,
    TRIAL_STORM_LIMIT,
    // Tier helpers
    isPro,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
